import React, { FC } from 'react';
import { Box, Link, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { palette } from '../../../theme';

export type OnboardingRole = 'athlete' | 'guardian';

export interface RoleSelectionStepProps {
  selectedRole: OnboardingRole | null;
  onRoleChange: (role: OnboardingRole) => void;
  onBack: () => void;
  onNext: () => void;
}

const GUARDIAN_ORANGE = '#E06A10';

interface RoleCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  selected: boolean;
  accentColor: string;
  onClick: () => void;
}

const RoleCard: FC<RoleCardProps> = ({ icon, title, subtitle, selected, accentColor, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      p: 2,
      borderRadius: 2,
      border: `1.5px solid ${selected ? accentColor : palette.grey[200]}`,
      backgroundColor: selected ? accentColor : '#fff',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      mb: 1.5,
      '&:hover': {
        borderColor: accentColor,
        backgroundColor: selected ? accentColor : `${accentColor}12`,
      },
    }}
  >
    {/* Icon badge */}
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        backgroundColor: selected ? 'rgba(255,255,255,0.22)' : `${accentColor}18`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon as React.ReactElement, {
        sx: { color: selected ? '#fff' : accentColor, fontSize: 22 },
      })}
    </Box>

    {/* Text */}
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="body1"
        fontWeight={600}
        sx={{ color: selected ? '#fff' : palette.text.primary, lineHeight: 1.3 }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: selected ? 'rgba(255,255,255,0.82)' : palette.text.secondary, fontSize: 13 }}
      >
        {subtitle}
      </Typography>
    </Box>

    <ChevronRightIcon sx={{ color: selected ? 'rgba(255,255,255,0.7)' : palette.grey[400], fontSize: 20 }} />
  </Box>
);

const RoleSelectionStep: FC<RoleSelectionStepProps> = ({ selectedRole, onRoleChange, onBack, onNext }) => {
  return (
    <Box>
      {/* Back link */}
      <Link
        component="button"
        onClick={onBack}
        underline="none"
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          fontSize: 14,
          mb: 3,
          '&:hover': { color: 'text.primary' },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 18, mr: 0.25 }} />
        Back
      </Link>

      <Typography variant="h5" fontWeight={700} mb={0.5}>
        What&apos;s your role?
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        This helps us personalize your experience.
      </Typography>

      {/* Role cards */}
      <RoleCard
        icon={<PersonOutlineIcon />}
        title="Athlete / Coach"
        subtitle="Set up your own training profile and connect with teams."
        selected={selectedRole === 'athlete'}
        accentColor={palette.primary.main}
        onClick={() => onRoleChange('athlete')}
      />
      <RoleCard
        icon={<ShieldOutlinedIcon />}
        title="Guardian"
        subtitle="Monitor and support your athlete's training journey."
        selected={selectedRole === 'guardian'}
        accentColor={GUARDIAN_ORANGE}
        onClick={() => onRoleChange('guardian')}
      />

      {/* Bottom navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
        <Link
          component="button"
          onClick={onBack}
          underline="none"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            fontSize: 14,
            '&:hover': { color: 'text.primary' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18, mr: 0.25 }} />
          Back
        </Link>

        <Link
          component="button"
          onClick={() => selectedRole && onNext()}
          underline="none"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: selectedRole ? palette.primary.main : palette.grey[400],
            fontWeight: 600,
            fontSize: 14,
            cursor: selectedRole ? 'pointer' : 'default',
            '&:hover': { color: selectedRole ? palette.primary.dark : palette.grey[400] },
          }}
        >
          Next
          <ChevronRightIcon sx={{ fontSize: 18, ml: 0.25 }} />
        </Link>
      </Box>
    </Box>
  );
};

export default RoleSelectionStep;
